import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, timeAgo, formatApiError } from "../lib/api";
import Pager from "./Pager";

const STATUSES = ["new", "contacted", "scheduled", "done", "cancelled"];
const STATUS_STYLE = {
  new: "bg-blue-50 text-blue-700",
  contacted: "bg-amber-50 text-amber-700",
  scheduled: "bg-green-50 text-green-700",
  done: "bg-zinc-100 text-zinc-600",
  cancelled: "bg-zinc-100 text-zinc-400",
};

export function DemoBookings() {
  const [cfg, setCfg] = useState(null);
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [saving, setSaving] = useState(false);
  const [inviteFor, setInviteFor] = useState(null);
  const [inviteForm, setInviteForm] = useState({ date: "", time: "", duration: 30, note: "" });

  const loadCfg = () => api.get("/admin/demo/settings").then((r) => setCfg(r.data)).catch(() => toast.error("Couldn't load demo settings"));
  const load = (p = page, s = statusFilter) =>
    api.get("/admin/demo/requests", { params: { page: p, status: s } }).then((r) => setData(r.data)).catch(() => toast.error("Couldn't load demo requests"));

  useEffect(() => { loadCfg(); load(1, ""); }, []);

  const saveCfg = async () => {
    setSaving(true);
    try {
      await api.put("/admin/demo/settings", cfg);
      toast.success(cfg.enabled ? "Demo button is live on the site" : "Demo button hidden from the site");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/demo/requests/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const sendInvite = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/admin/demo/requests/${inviteFor.id}/invite`, inviteForm);
      toast.success(`Invite sent, demo booked for ${r.data.when}`);
      setInviteFor(null);
      setInviteForm({ date: "", time: "", duration: 30, note: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!cfg) return <div className="h-48 rounded-md bg-zinc-50 animate-pulse" data-testid="demo-loading" />;

  return (
    <div className="space-y-5" data-testid="demo-bookings">
      <div className="bg-white border border-zinc-200 rounded-lg p-5">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-base font-bold text-zinc-950">Book a demo button</h3>
          <label className="flex items-center gap-2 text-xs font-semibold text-zinc-600 cursor-pointer"
            title="When on, a small floating button appears at the bottom-left of every public page">
            <input type="checkbox" checked={cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              data-testid="demo-enabled" className="accent-zinc-900" />
            Show on site
          </label>
        </div>
        <p className="text-xs text-zinc-500 mb-4 max-w-xl">
          Visitors who click it see a short form; every request lands below, emails you at office@hiredtoday.co.uk, and sends the visitor a confirmation.
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Button label</label>
            <input value={cfg.button_label} onChange={(e) => setCfg({ ...cfg, button_label: e.target.value })}
              placeholder="Book a demo" data-testid="demo-btn-label"
              className="mt-1 w-full h-9 px-3 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Form heading</label>
            <input value={cfg.heading} onChange={(e) => setCfg({ ...cfg, heading: e.target.value })}
              placeholder="See HiredToday in action" data-testid="demo-heading-input"
              className="mt-1 w-full h-9 px-3 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
          </div>
          <div className="sm:col-span-3">
            <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Form intro line</label>
            <input value={cfg.subtext} onChange={(e) => setCfg({ ...cfg, subtext: e.target.value })}
              placeholder="A friendly 20-minute walkthrough…" data-testid="demo-subtext-input"
              className="mt-1 w-full h-9 px-3 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <button onClick={saveCfg} disabled={saving} data-testid="demo-settings-save"
            className="h-9 px-5 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors duration-200">
            {saving ? "Saving…" : "Save"}
          </button>
          {cfg.updated_at && <span className="text-[11px] text-zinc-400">Saved {timeAgo(cfg.updated_at)}</span>}
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-5" data-testid="demo-requests">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-display text-base font-bold text-zinc-950">
            Requests {data && <span className="text-zinc-400 font-semibold text-sm">· {data.new_count} new · {data.scheduled_count} scheduled</span>}
          </h3>
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); load(1, e.target.value); }}
            data-testid="demo-status-filter"
            className="h-8 px-2 text-xs border border-zinc-300 rounded-md bg-white">
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {!data || data.items.length === 0 ? (
          <p className="text-sm text-zinc-400" data-testid="demo-empty">No demo requests yet, switch the button on and share the site.</p>
        ) : (
          <>
            <div className="space-y-2">
              {data.items.map((r) => (
                <div key={r.id} className="border border-zinc-100 rounded-md p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`demo-req-${r.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-zinc-900">
                      {r.name}{r.company && <span className="font-normal text-zinc-500"> · {r.company}</span>}
                    </p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      <a href={`mailto:${r.email}`} className="text-blue-700 hover:underline">{r.email}</a>
                      {r.phone && <> · {r.phone}</>} · {r.preferred} · {timeAgo(r.created_at)}
                    </p>
                    {r.note && <p className="text-xs text-zinc-600 mt-1.5 leading-relaxed">“{r.note}”</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${STATUS_STYLE[r.status]}`}
                      data-testid={`demo-req-status-${r.id}`}>{r.status}</span>
                    {r.status !== "done" && r.status !== "cancelled" && (
                      <button onClick={() => { setInviteFor(r); setInviteForm({ date: "", time: "", duration: 30, note: "" }); }}
                        data-testid={`demo-req-invite-${r.id}`}
                        title="Email them a confirmed slot with a calendar invite, moves the request to scheduled"
                        className="h-8 px-3 text-xs font-semibold bg-zinc-950 text-white rounded-md hover:bg-zinc-800 transition-colors duration-200">
                        Send invite
                      </button>
                    )}
                    <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)} data-testid={`demo-req-set-${r.id}`}
                      title="Update where this request is in your pipeline"
                      className="h-8 px-2 text-xs border border-zinc-300 rounded-md bg-white">
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
            <Pager page={page} pages={data.pages} onPage={(p) => { setPage(p); load(p); }} />
          </>
        )}
      </div>

      {inviteFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="demo-invite-modal" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-zinc-950/50 backdrop-blur-sm" onClick={() => setInviteFor(null)} />
          <div className="relative bg-white rounded-xl border border-zinc-200 shadow-2xl w-full max-w-sm p-6">
            <p className="font-display text-lg font-bold text-zinc-950">Confirm a slot for {inviteFor.name}</p>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
              They'll get a branded email with the confirmed time and a calendar invite they can add in one tap. The request moves to <strong>scheduled</strong>.
              {inviteFor.preferred && <> They asked for: <strong>{inviteFor.preferred}</strong>.</>}
            </p>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div>
                <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Date</label>
                <input type="date" value={inviteForm.date} data-testid="invite-date"
                  onChange={(e) => setInviteForm((f) => ({ ...f, date: e.target.value }))}
                  className="mt-1 w-full h-9 px-3 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Time (UK)</label>
                <input type="time" value={inviteForm.time} data-testid="invite-time"
                  onChange={(e) => setInviteForm((f) => ({ ...f, time: e.target.value }))}
                  className="mt-1 w-full h-9 px-3 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
              </div>
              <div className="col-span-2">
                <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Length</label>
                <select value={inviteForm.duration} data-testid="invite-duration"
                  onChange={(e) => setInviteForm((f) => ({ ...f, duration: Number(e.target.value) }))}
                  className="mt-1 w-full h-9 px-3 text-sm border border-zinc-300 rounded-md bg-white focus:outline-none focus:border-zinc-900">
                  {[15, 20, 30, 45, 60].map((d) => <option key={d} value={d}>{d} minutes</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Note to them (optional)</label>
                <textarea rows={2} value={inviteForm.note} data-testid="invite-note"
                  onChange={(e) => setInviteForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="e.g. We'll send you a video link on the day."
                  className="mt-1 w-full px-3 py-2 text-sm border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={sendInvite} disabled={saving || !inviteForm.date || !inviteForm.time} data-testid="invite-send"
                className="flex-1 h-10 bg-zinc-950 text-white text-xs font-semibold rounded-md hover:bg-zinc-800 disabled:opacity-50 transition-colors duration-200">
                {saving ? "Sending…" : "Send calendar invite"}
              </button>
              <button onClick={() => setInviteFor(null)} data-testid="invite-cancel"
                className="h-10 px-4 border border-zinc-300 text-xs font-semibold rounded-md hover:border-zinc-500 transition-colors duration-200">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
